/**
 * Vue I18n with lazy loading from API v2.
 * Messages are loaded per namespace from GET /api/v2/translations/{locale}/{namespace}.
 */

import { createI18n } from 'vue-i18n';
import { compile } from '@intlify/core-base';
import axios from 'axios';

const STORAGE_PREFIX = 'trans';
const API_BASE = '/api/v2';

/** Track loaded locale:namespace to skip redundant API calls (back/forward, navigate after redirect). */
const loadedBundles = new Set();

function loadedKey(locale, namespace) {
  return `${locale}:${namespace}`;
}

function getLocale() {
  return document.querySelector('meta[name="site-language"]')?.getAttribute('content')
    || window.LOCALE
    || 'en';
}

/**
 * Public-facing brand/site name (single source of truth: config('app.brand'),
 * exposed via the `site-name` meta tag). Translation values use the {siteName}
 * placeholder, which is substituted with this value when messages are merged —
 * so the whole UI rebrands by changing one key, with no per-key edits and
 * without every $t() call having to pass a siteName param. Other placeholders
 * (e.g. {target}) are left untouched for normal vue-i18n interpolation.
 */
function getSiteName() {
  return document.querySelector('meta[name="site-name"]')?.getAttribute('content')
    || window.SITE_NAME
    || '';
}

const SITE_NAME_TOKEN = '{siteName}';
const SITE_NAME = getSiteName();

function storageKey(locale, namespace) {
  return `${STORAGE_PREFIX}:${locale}:${namespace}`;
}

function storageVersionKey(locale, namespace) {
  return `${STORAGE_PREFIX}:${locale}:${namespace}:version`;
}

/**
 * Load translations for one namespace from API (or localStorage if valid version).
 * Merges into i18n global scope as flat keys (e.g. common.buttons.save => value).
 */
async function loadNamespace(i18n, locale, namespace) {
  if (loadedBundles.has(loadedKey(locale, namespace))) return {};
  const key = storageKey(locale, namespace);
  const versionKey = storageVersionKey(locale, namespace);
  const cached = localStorage.getItem(key);
  const cachedVersion = localStorage.getItem(versionKey);

  try {
    const url = `${API_BASE}/translations/${encodeURIComponent(locale)}/${encodeURIComponent(namespace)}`;
    const headers = {};
    if (cachedVersion) headers['If-None-Match'] = `"${cachedVersion}"`;

    const res = await axios.get(url, { headers, validateStatus: (s) => s === 200 || s === 304 });
    if (res.status === 304 && cached) {
      const messages = JSON.parse(cached);
      mergeMessages(i18n, locale, messages);
      loadedBundles.add(loadedKey(locale, namespace));
      return messages;
    }
    const messages = res.data && typeof res.data === 'object' ? res.data : {};
    const etag = res.headers?.etag;
    if (etag && typeof etag === 'string') {
      const version = etag.replace(/^"|"$/g, '');
      try {
        localStorage.setItem(key, JSON.stringify(messages));
        localStorage.setItem(versionKey, version);
      } catch (e) {
        // quota or disabled
      }
    }
    mergeMessages(i18n, locale, messages);
    loadedBundles.add(loadedKey(locale, namespace));
    return messages;
  } catch (err) {
    if (cached) {
      try {
        const messages = JSON.parse(cached);
        mergeMessages(i18n, locale, messages);
        loadedBundles.add(loadedKey(locale, namespace));
        return messages;
      } catch (e) {
        // ignore
      }
    }
    mergeMessages(i18n, locale, {});
    return {};
  }
}

/**
 * Load multiple namespaces in one request via bundle API when possible.
 * Skips namespaces already loaded for this locale (avoids redundant requests on back/forward).
 */
async function loadBundle(i18n, locale, namespaces) {
  if (!namespaces || namespaces.length === 0) return {};
  const needed = namespaces.filter((ns) => !loadedBundles.has(loadedKey(locale, ns)));
  if (needed.length === 0) return {};
  if (needed.length === 1) {
    return loadNamespace(i18n, locale, needed[0]);
  }

  const bundleKey = [...needed].sort().join(',');
  const cacheKey = `${STORAGE_PREFIX}:${locale}:bundle:${bundleKey}`;
  const versionKey = `${cacheKey}:versions`;

  try {
    const cached = localStorage.getItem(cacheKey);
    const cachedVersions = localStorage.getItem(versionKey);

    // Always fetch bundle directly (single request). If we have cached versions,
    // send them so the server can skip unchanged namespaces in the future.
    const url = `${API_BASE}/translations/${encodeURIComponent(locale)}/bundle?namespaces=${encodeURIComponent(needed.join(','))}`;
    const headers = {};
    if (cached && cachedVersions) {
      headers['X-Cached-Versions'] = cachedVersions;
    }
    const res = await axios.get(url, { headers });
    const data = res.data || {};
    const messages = data.messages && typeof data.messages === 'object' ? data.messages : {};
    const versions = data.versions && typeof data.versions === 'object' ? data.versions : {};

    // If server returned empty messages but we have cache, check versions
    if (Object.keys(messages).length === 0 && cached && cachedVersions) {
      try {
        const stored = JSON.parse(cachedVersions);
        let allMatch = true;
        for (const ns of needed) {
          if ((stored[ns] || null) !== (versions[ns] || null)) {
            allMatch = false;
            break;
          }
        }
        if (allMatch) {
          const cachedMessages = JSON.parse(cached);
          mergeMessages(i18n, locale, cachedMessages);
          needed.forEach((ns) => loadedBundles.add(loadedKey(locale, ns)));
          return cachedMessages;
        }
      } catch (e) {
        // ignore
      }
    }

    mergeMessages(i18n, locale, messages);
    needed.forEach((ns) => loadedBundles.add(loadedKey(locale, ns)));
    try {
      localStorage.setItem(cacheKey, JSON.stringify(messages));
      localStorage.setItem(versionKey, JSON.stringify(versions));
    } catch (e) {
      // quota or disabled
    }
    return messages;
  } catch (err) {
    if (localStorage.getItem(cacheKey)) {
      try {
        const messages = JSON.parse(localStorage.getItem(cacheKey));
        mergeMessages(i18n, locale, messages);
        needed.forEach((ns) => loadedBundles.add(loadedKey(locale, ns)));
        return messages;
      } catch (e) {
        // ignore
      }
    }
    mergeMessages(i18n, locale, {});
    return {};
  }
}

/**
 * Set a value at a dotted path in an object.
 */
function setNested(obj, path, value) {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!(p in cur) || typeof cur[p] !== 'object') cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

/**
 * Convert flat key-value to nested and deep-merge into target.
 */
function mergeMessages(i18n, locale, flatMessages) {
  if (!i18n.global) return;
  const localeMessages = i18n.global.getLocaleMessage(locale) || {};
  const nested = JSON.parse(JSON.stringify(localeMessages));
  for (const [key, value] of Object.entries(flatMessages)) {
    if (typeof value === 'string') {
      const resolved = value.includes(SITE_NAME_TOKEN)
        ? value.split(SITE_NAME_TOKEN).join(SITE_NAME)
        : value;
      setNested(nested, key, resolved);
    }
  }
  i18n.global.setLocaleMessage(locale, nested);
}

/**
 * Clear client-side translation cache (localStorage), including bundle keys.
 */
function clearTranslationCache() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX + ':')) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // ignore
  }
}

/** Clear in-memory loaded namespaces tracking (e.g. when forcing re-fetch on locale change). */
function clearLoadedTracking() {
  loadedBundles.clear();
}

/** Check whether a specific namespace has been loaded for a locale. */
function isNamespaceLoaded(locale, namespace) {
  return loadedBundles.has(loadedKey(locale, namespace));
}

/**
 * Synchronously load translations from localStorage into i18n (no network).
 * Used during navigation to prevent key flash: localStorage data is merged
 * immediately so $t() returns translated text before the browser paints,
 * while the async loadBundle verifies freshness in the background.
 */
function loadFromCacheSync(i18n, locale, namespaces) {
  if (!namespaces || namespaces.length === 0) return;
  const needed = namespaces.filter((ns) => !loadedBundles.has(loadedKey(locale, ns)));
  if (needed.length === 0) return;

  for (const ns of needed) {
    const key = storageKey(locale, ns);
    const cached = localStorage.getItem(key);
    if (cached) {
      try {
        const messages = JSON.parse(cached);
        mergeMessages(i18n, locale, messages);
      } catch (e) {
        // corrupt cache, skip
      }
    }
  }

  // Also check bundle caches (multi-namespace bundles stored together)
  const bundleKey = [...needed].sort().join(',');
  const bundleCacheKey = `${STORAGE_PREFIX}:${locale}:bundle:${bundleKey}`;
  const bundleCached = localStorage.getItem(bundleCacheKey);
  if (bundleCached) {
    try {
      const messages = JSON.parse(bundleCached);
      mergeMessages(i18n, locale, messages);
    } catch (e) {
      // ignore
    }
  }
}

/**
 * Resilient message compiler.
 *
 * vue-i18n treats '@' as the start of a linked-message reference (e.g.
 * "@:other.key"). This app uses no linked messages, so the only '@' in our
 * strings are literal — mostly email placeholders like you@email.com — which
 * would otherwise throw INVALID_LINKED_FORMAT (compile error code 10) and crash
 * the whole page render (including when translations are missing and a literal
 * fallback string is compiled). We escape '@' to its literal {'@'} form before
 * compiling, and fall back to the raw text on any other compile error, so a
 * single malformed string can never take a page down.
 */
function messageCompiler(message, context) {
  if (typeof message !== 'string') return compile(message, context);
  const safe = message.includes('@') ? message.split('@').join("{'@'}") : message;
  try {
    return compile(safe, context);
  } catch (e) {
    return () => message;
  }
}

const locale = getLocale();
const i18n = createI18n({
  legacy: false,
  locale,
  fallbackLocale: false,
  silentTranslationWarn: true,
  messages: { [locale]: {} },
  missing: (_locale, key) => key,
  messageCompiler,
});

if (typeof window !== 'undefined') {
  window.I18nDebug = { enable: () => {}, disable: () => {} };
}

export default i18n;
export { loadNamespace, loadBundle, loadFromCacheSync, clearTranslationCache, clearLoadedTracking, getLocale, isNamespaceLoaded };
